/**
 * What the student has been round asking, of whom, and what was said back.
 *
 * `talkStore` keeps ONE conversation and wipes its transcript the moment
 * the student walks away, which is right for the panel - a chat you left is
 * over - and leaves nobody on the floor with any idea what the student has
 * been doing. A shift supervisor who has watched somebody go from tool to
 * tool for twenty minutes is the most useful person in a fab to a beginner,
 * and he had less to go on than the beginner did. Worse, the technician who
 * has just spent five minutes explaining selectivity has forgotten it by
 * the time the student walks back in, and explains it again in the same
 * words - which is the one thing a good instructor never does.
 *
 * So: one session-scoped list of the stops, across everybody. It is read
 * two ways, and the difference between them is the whole design.
 *
 * `entries()` - THE ROUND, for the shift supervisor. The STUDENT'S side and
 * nothing else: what they asked, who they asked, and how it turned out. Not
 * one word of what any technician replied.
 *
 * That is not squeamishness about storage, it is the same rule the rest of
 * the brief follows. A technician speaks for the machine in front of them;
 * a tool's readings belong to whoever is standing at it (see TALK.md, and
 * the `status` field in brief.ts, which is state and never numbers). If the
 * supervisor could read back what Sarah answered about ETCH-01, he would be
 * quoting a reading off a tool he has not been at, in someone else's words,
 * with nothing in the scene to back it - which is the one thing this whole
 * system is built to prevent. Questions are the student's own words and
 * carry no claim about any tool, so they are safe to hand on.
 *
 * `taughtBy(who)` - WHAT ONE PERSON HAS ALREADY BEEN OVER, for that person
 * and nobody else. Their own questions and THEIR OWN REPLIES, because a
 * technician remembering what they themselves said twenty minutes ago is
 * not a leak of anything: they said it, out loud, to this student, standing
 * where they are standing now. It is the least controversial fact in the
 * building, and it is the one this system was missing.
 *
 * The two views are separate TYPES, not a convention. `entries` returns
 * `RoundEntry`, which has no field for anybody's words at all, so the round
 * cannot start carrying a reply through a careless edit in brief.ts: the
 * words are not in the object it is handed.
 *
 * NOT PERSISTED. The ask-box history in `talkStore` is kept because
 * retyping a question is a real cost; a technician's memory of a shift is
 * not, and a shared lab machine should not open with the last student's
 * questions in a stranger's brief. It dies with the tab, and "Start a new
 * session" reloads, so that clears it too.
 */

/** How a question turned out, from the outside. */
export type TalkOutcome =
  /** They answered. */
  | 'answered'
  /** They answered and ran it on the tool. */
  | 'ran'
  /** The student cut it off, or walked away before the answer landed. */
  | 'stopped'

/** A stop on the round, as anyone watching the aisle could describe it. */
export interface RoundEntry {
  /** Cast id of the person asked, so a reader can leave themselves out. */
  who: string
  /** The asset tag they were standing at, or '' for someone on the move. */
  tag: string
  /** The student's own words. */
  asked: string
  outcome: TalkOutcome
  /** When, so "ten minutes ago" is answerable. */
  at: number
}

/**
 * What is actually stored: the stop, plus the two things only the person who
 * was standing there may have back, plus its place in the order.
 *
 * Not exported. Both readers project, and the projections are what the
 * types above and below describe - which is the point: a reply cannot reach
 * the supervisor's brief because it is not in the object he is handed.
 */
interface Stop extends RoundEntry {
  /** What THEY said back, trimmed. Empty if the student never got an answer. */
  said: string
  /** The scene they put on the screen, if they put one up (a demo id). */
  scene: string
  /**
   * Where this falls in the order, counted rather than clocked.
   *
   * `at` is a wall clock and two things in the same millisecond compare
   * equal, which is exactly the comparison `taughtBy` makes when it decides
   * whether a stop belongs to the conversation on screen or to memory. A
   * counter cannot tie, and cannot go backwards if the clock does.
   */
  seq: number
}

/**
 * A stop as the brief carries it: minutes ago rather than a timestamp, and
 * without the `who` - the only person handed this is the one it happened to.
 */
export interface TaughtStop {
  /** The student's own words, or '' for a line nobody asked for. */
  asked: string
  /** What this person said back, or '' if the student left before an answer. */
  said: string
  /** The scene they put up, if any (a demo id, resolved to its title later). */
  scene: string
  outcome: TalkOutcome
  /** Where they were standing when it happened. */
  tag: string
  /** Minutes ago, because that is how anybody remembers a shift. */
  ago: number
}

/** One person's memory of this student, as the brief carries it. */
export interface Taught {
  /** Conversations, this one included. A knock does not count as coming to them. */
  visits: number
  /** What they covered, oldest first. Never the conversation happening now. */
  covered: TaughtStop[]
}

/**
 * Bounded twice over: what is kept, and what any one reader is handed.
 * A shift is long and the brief has a hard 8 KB cap at the proxy.
 */
const KEEP = 24
/**
 * Longest question kept. The ask box takes 500, but the round is measured
 * against the proxy's 8 KB brief cap alongside the tool directory and the
 * scene list (`npm run talk` asserts the share), and 120 characters is
 * plenty to recognise a question by. Longer ones are the student thinking
 * aloud, and the tail of that is not what a supervisor would remember.
 */
const ASKED_MAX = 120
/**
 * Longest reply kept, and shorter than it sounds on purpose: this is a
 * technician's memory of having covered something, not a transcript to read
 * back. The opening clause of what they said is enough to know which ground
 * it was, and the prompt tells them in as many words not to quote it. Held
 * with the rest against the proxy cap: the supervisor carries this, the
 * round and the tool tally at once, and `npm run talk` asserts the total.
 */
const SAID_MAX = 120
/**
 * How many stops one person remembers of this student.
 *
 * Was three, on the argument that anybody who has been over three things and
 * is still starting from the beginning has a worse problem than a short
 * memory. Watching it run said otherwise: six turns into one conversation at
 * ETCH-01 the scene Sarah had put up had already fallen out of her memory,
 * and she would have shown it again as though it were new. Six covers a
 * conversation that went somewhere without covering an afternoon.
 *
 * Six of them alongside the round and the tally is still what fits - see the
 * cap check in `npm run talk` - and `fitBrief` forgets the oldest first if
 * some future field makes it not.
 */
export const TAUGHT_MAX = 6

let entries: Stop[] = []
/**
 * Where the conversation on screen right now begins.
 *
 * Everything at or after this mark is already in the chat history the model
 * gets (`TALK_MAX_HISTORY` turns of it), so carrying it in `taught` as well
 * would hand the same words to the same model twice and spend the brief cap
 * doing it. `taught` is what happened BEFORE they walked back in.
 */
let mark = 0
/** The order things were said in. See `Stop.seq`. */
let seq = 0
/** Conversations opened with each person, knocks excluded. */
const visits = new Map<string, number>()

function push(entry: Omit<Stop, 'seq'>): void {
  entries = [...entries, { ...entry, seq: seq++ }].slice(-KEEP)
}

export const talkLog = {
  /**
   * A conversation opens. `unbidden` is a knock (watch.ts): the technician
   * called them over, so it is not the student coming to them and it does
   * not count as a visit - but it does end the last conversation, so the
   * mark moves either way.
   */
  arrive(who: string, unbidden = false): void {
    mark = seq
    if (!unbidden) visits.set(who, (visits.get(who) ?? 0) + 1)
  },

  /**
   * A question, at the moment it is asked. Returns nothing: the outcome is
   * settled later by `settle`, on whichever entry is still the last one.
   */
  ask(who: string, tag: string, asked: string): void {
    push({ who, tag, asked: asked.slice(0, ASKED_MAX), outcome: 'stopped', said: '', scene: '', at: Date.now() })
  },

  /**
   * A line nobody asked for - a knock the student took. There is no
   * question on it, so it is not a stop on the round and `entries` leaves
   * it out; but the person who said it has to remember having said it, or
   * they will say it again the moment the student comes back.
   */
  spoke(who: string, tag: string): void {
    push({ who, tag, asked: '', outcome: 'stopped', said: '', scene: '', at: Date.now() })
  },

  /**
   * How the last question turned out, and what was said back.
   *
   * Entries start as `stopped` and are upgraded, rather than the other way
   * round, because the case that leaves nothing to upgrade them IS the
   * student walking off mid-answer - `ask` returns early there and never
   * gets to say so.
   */
  settle(outcome: TalkOutcome, said = '', scene = ''): void {
    if (!entries.length) return
    const last = entries[entries.length - 1]
    entries = [...entries.slice(0, -1), { ...last, outcome, said: said.slice(0, SAID_MAX), scene }]
  },

  /**
   * The round so far, oldest first: the questions, and not one word of any
   * answer. A narrower type than what is stored, so a reply is not in the
   * object the supervisor's brief is built from.
   */
  entries(): RoundEntry[] {
    return entries.filter((e) => e.asked).map(({ who, tag, asked, outcome, at }) => ({ who, tag, asked, outcome, at }))
  },

  /**
   * What this one person has already been over with this student, before
   * the conversation now on screen. Null when they have never met, so the
   * brief carries nothing rather than an empty shape.
   */
  taughtBy(who: string): Taught | null {
    const mine = entries.filter((e) => e.who === who && e.seq < mark)
    const seen = visits.get(who) ?? 0
    // Nothing covered and no second visit is two strangers. One visit with
    // nothing covered is this conversation, which is not a memory yet.
    if (!mine.length && seen < 2) return null
    const now = Date.now()
    return {
      visits: seen,
      covered: mine.slice(-TAUGHT_MAX).map(({ asked, said, scene, outcome, tag, at }) => ({
        asked,
        said,
        scene,
        outcome,
        tag,
        ago: Math.max(0, Math.round((now - at) / 60_000)),
      })),
    }
  },

  /**
   * The scene this person has put on the screen IN THE CONVERSATION NOW ON
   * SCREEN, if they have put one up. The mirror of `taughtBy`: that one
   * reads everything before the mark, this one reads everything after it.
   *
   * Why the mark is the right boundary and not a timer: the card lives in
   * the transcript, and `talkStore.close()` wipes the transcript. A scene
   * from a conversation the student walked out of is not on any screen, and
   * the same rule that says its words are memory says its picture is gone.
   *
   * The LAST one, because putting a second scene up is what takes the first
   * one down - the panel marks the newest as live (`liveDemo` in
   * TalkPanel.tsx) and that is the one running.
   */
  showing(who: string): { scene: string; ago: number } | null {
    const up = entries.filter((e) => e.who === who && e.seq >= mark && e.scene).slice(-1)[0]
    return up ? { scene: up.scene, ago: Math.max(0, Math.round((Date.now() - up.at) / 60_000)) } : null
  },

  /** For a harness, and for a session that starts over without a reload. */
  clear(): void {
    entries = []
    mark = 0
    seq = 0
    visits.clear()
  },
}
